import { useState, useCallback, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { OpenFundRequestDtoIn } from '@/models/in/OpenFundRequestDtoIn';
import { OpenFundRequestStatus } from '@/models/common/OpenFundRequestStatus';
import { FundClassification } from '@/models/common/FundClassification';
import { FundGoals } from '@/models/common/FundGoals';
import { SyncStatus } from '@/models/common/SyncStatus';
import { OpenFundRequestDocumentSlot } from '@/models/common/OpenFundRequestDocumentSlot';
import { OpenFundRequestPersonRole } from '@/models/common/OpenFundRequestPersonRole';
import { IdRedactionStatus } from '@/models/common/IdRedactionStatus';
import { DocumentDtoIn } from '@/models/in/DocumentDtoIn';
import { useDeleteAuthApi, useGetAuthApi, usePutAuthApi } from '@/hooks/UseApiCalls';
import { queryClient } from '@/hooks/QueryClient';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { downloadFileFromApi } from '@/helpers/downloadFile';
import Button from '@/components/ui/Button';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import CustomDialog from '@/components/ui/Dialog/Dialog';
import DownloadFileButton from '@/components/ui/DownloadFileButton/DownloadFileButton';
import downloadArrow from '@/assets/icons/download-arrow.svg';

import OpenFundRequestEditDialog from './OpenFundRequestEditDialog';
import ReplaceIdDocumentDialog, { IdDocumentTarget } from './ReplaceIdDocumentDialog';
import CropIdDocumentDialog, { CropTarget } from './CropIdDocumentDialog';
import RedactIdDocumentDialog, { RedactionTarget } from './RedactIdDocumentDialog';
import styles from './OpenFundRequestViewPage.module.scss';

/**
 * The section icons are drawn inline rather than loaded as <img>, because they sit on the purple heading bar
 * and have to be white - an <img> cannot be recoloured, and the icon set on disk comes in four colours.
 */
type SectionIconName = 'fund' | 'purpose' | 'applicant' | 'coordinator' | 'supported';

const SectionIcon = ({ name }: { name: SectionIconName }) => {
  const props = {
    width: 20,
    height: 20,
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (name) {
    case 'fund':
      return (
        <svg {...props} viewBox="0 0 24 24" className={styles.sectionIcon}>
          <path d="M5 8.625H8.62499V12.25H5V8.625Z" />
          <path d="M6.81262 5V8.62499" />
          <path d="M6.81262 12.25V19.5" />
          <path d="M10.4375 14.0625H14.0625V17.6875H10.4375V14.0625Z" />
          <path d="M12.2501 5V14.0625" />
          <path d="M12.2501 17.6875V19.5" />
          <path d="M15.875 5.90631H19.5V9.53131H15.875V5.90631Z" />
          <path d="M17.6876 5V5.90625" />
          <path d="M17.6876 9.53125V19.5" />
        </svg>
      );
    case 'purpose':
      return (
        <svg {...props} viewBox="0 0 24 24" className={styles.sectionIcon}>
          <path d="M19.4967 13.573L12.0021 20.9957L4.5074 13.573C4.01306 13.092 3.62367 12.5138 3.36377 11.8749C3.10386 11.2359 2.97905 10.5501 2.99722 9.8606C3.01538 9.17107 3.17611 8.49278 3.4693 7.86842C3.76248 7.24407 4.18177 6.68718 4.70075 6.23283C5.21973 5.77848 5.82717 5.43651 6.4848 5.22845C7.14244 5.02039 7.83603 4.95075 8.52191 5.02392C9.20778 5.09708 9.87108 5.31147 10.47 5.65357C11.069 5.99568 11.5906 6.45809 12.0021 7.0117C12.4153 6.46211 12.9375 6.00374 13.5361 5.66526C14.1346 5.32679 14.7966 5.1155 15.4805 5.04462C16.1645 4.97374 16.8557 5.0448 17.5109 5.25334C18.1661 5.46189 18.7713 5.80343 19.2884 6.2566C19.8056 6.70977 20.2236 7.2648 20.5164 7.88697C20.8092 8.50913 20.9704 9.18503 20.99 9.87237C21.0095 10.5597 20.887 11.2437 20.63 11.8815C20.3731 12.5193 19.9873 13.0972 19.4967 13.579" />
          <path d="M12.0021 7.00568L8.7114 10.2963C8.52406 10.4837 8.41882 10.7379 8.41882 11.0028C8.41882 11.2678 8.52406 11.5219 8.7114 11.7093L9.25401 12.2519C9.94352 12.9414 11.0627 12.9414 11.7522 12.2519L12.7515 11.2526C13.3478 10.6563 14.1566 10.3213 14.9999 10.3213C15.8432 10.3213 16.652 10.6563 17.2483 11.2526L19.4967 13.501" />
          <path d="M12.5017 16.499L14.5003 18.4975" />
          <path d="M14.9999 14.0007L16.9985 15.9992" />
        </svg>
      );
    case 'applicant':
      return (
        <svg {...props} viewBox="0 0 20 20" className={styles.sectionIcon}>
          <path d="M15.3033 4.6967C18.2322 7.62563 18.2322 12.3744 15.3033 15.3033C12.3743 18.2322 7.62562 18.2322 4.6967 15.3033C1.76777 12.3743 1.76777 7.62562 4.6967 4.6967C7.62563 1.76777 12.3744 1.76777 15.3033 4.6967Z" />
          <path d="M11.6576 6.93659C12.573 7.85204 12.573 9.33629 11.6576 10.2517C10.7421 11.1672 9.25789 11.1672 8.34244 10.2517C7.42699 9.33629 7.42699 7.85204 8.34244 6.93659C9.25789 6.02114 10.7421 6.02114 11.6576 6.93659Z" />
          <path d="M14.7558 15.7983C13.56 14.5392 11.8733 13.75 10 13.75C8.12667 13.75 6.44 14.5392 5.24417 15.7992" />
        </svg>
      );
    case 'coordinator':
      return (
        <svg {...props} viewBox="0 0 24 24" strokeWidth={1.6} className={styles.sectionIcon}>
          <circle cx="8.6" cy="7" r="3.3" />
          <path d="M3.3 21V17.7C3.3 14.77 5.67 12.4 8.6 12.4C11.53 12.4 13.9 14.77 13.9 17.7V21" />
          <circle cx="17.6" cy="10.1" r="2.6" />
          <path d="M14.6 21V18.7C14.6 17.04 15.94 15.7 17.6 15.7C19.26 15.7 20.6 17.04 20.6 18.7V21" />
        </svg>
      );
    case 'supported':
      return (
        <svg {...props} viewBox="0 0 24 24" className={styles.sectionIcon}>
          <path d="M9.44378 8.7429C10.1033 9.40243 10.1033 10.4709 9.44378 11.1304C8.78426 11.7899 7.71581 11.7899 7.05629 11.1304C6.39676 10.4709 6.39676 9.40243 7.05629 8.7429C7.71581 8.08338 8.78426 8.08338 9.44378 8.7429Z" />
          <path d="M11.4921 15.4072C11.3263 14.365 10.4304 13.5667 9.34249 13.5667H7.15758C6.06888 13.5667 5.17375 14.365 5.00793 15.4072" />
          <path d="M18.7516 18.7528H5.24844C3.591 18.7528 2.24719 17.409 2.24719 15.7516V8.24844C2.24719 6.591 3.591 5.24719 5.24844 5.24719H18.7516C20.409 5.24719 21.7528 6.591 21.7528 8.24844V15.7516C21.7528 17.409 20.409 18.7528 18.7516 18.7528Z" />
          <path d="M13.125 10.125H16.125" />
          <path d="M13.125 13.125H19.125" />
        </svg>
      );
  }
};

const CheckMark = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className={styles.tileMark}>
    <path d="M5 12.5L10 17.5L19 7.5" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CrossMark = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className={styles.tileMark}>
    <path d="M6 6L18 18" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" />
    <path d="M18 6L6 18" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" />
  </svg>
);

/** One plain detail: a quiet label with its value below it. Editing happens in the edit dialog. */
const Field = ({ label, value, wide }: { label: string; value?: ReactNode; wide?: boolean }) => {
  const isEmpty = value === null || value === undefined || value === '';
  return (
    <div className={wide ? styles.fieldWide : styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <span className={isEmpty ? styles.fieldValueEmpty : styles.fieldValue}>{isEmpty ? '—' : value}</span>
    </div>
  );
};

/**
 * What the fund is set up for, stated as a fact rather than as a question with a כן/לא answer - a question
 * makes the reader take in two lines to learn one thing.
 */
const SettingTile = ({ text, on }: { text: string; on?: boolean | null }) => (
  <div className={styles.settingTile}>
    {on ? <CheckMark /> : <CrossMark />}
    <span className={styles.settingText}>{text}</span>
  </div>
);

/**
 * An attached file, with the download glyph on the tile itself. The identity documents also carry the
 * action that replaces them, so a bad ID copy is fixed where it is read rather than through the edit dialog,
 * and the coordinator's ID appendix carries the action that destroys it - the office reads it and is not
 * meant to keep it.
 */
const DocChip = ({ label, document: doc, onReplace, onDelete, onCrop, redaction, onRedact }: {
  label: string;
  document?: DocumentDtoIn | null;
  onReplace?: () => void;
  /** Offered only where the file itself is not meant to be kept; absent on the tiles that have to hold one. */
  onDelete?: () => void;
  /**
   * Offered where the file has to be kept but may be carrying something that must not be - a coordinator's
   * ID card with the appendix photographed onto it (HAT-1302).
   */
  onCrop?: () => void;
  /** Where this ID-card copy stands on having its issue date blacked out; absent on every other file. */
  redaction?: IdRedactionStatus;
  onRedact?: () => void;
}) => {
  // A copy that still shows its issue date arrives named but not linked (HAT-1303). It is described here
  // and opened only in the redaction screen - the office is not given an address it could save it from.
  if (redaction === IdRedactionStatus.Pending && onRedact) {
    return (
      <div className={styles.field}>
        <span className={styles.fieldLabel}>{label}</span>
        <div className={styles.docChip}>
          <span className={styles.docChipLink}>{doc?.name ?? '—'}</span>
          <span className={styles.redactBadge}>ממתין להשחרה</span>
          <button type="button" className={styles.docChipAction} onClick={onRedact}>השחרה</button>
          {/* Offered here too, and not only once the copy is redacted: cutting the appendix off is better
              done before the date is burned in than after. */}
          {onCrop && (
            <button type="button" className={styles.docChipAction} onClick={onCrop}>חיתוך</button>
          )}
          {onReplace && (
            <button type="button" className={styles.docChipAction} onClick={onReplace}>החלפה</button>
          )}
        </div>
      </div>
    );
  }

  if (!doc?.uri) {
    return (
      <div className={styles.field}>
        <span className={styles.fieldLabel}>{label}</span>
        {onReplace ? (
          <div className={styles.docChip}>
            <span className={`${styles.docChipLink} ${styles.fieldValueEmpty}`}>—</span>
            <button type="button" className={styles.docChipAction} onClick={onReplace}>העלאה</button>
          </div>
        ) : (
          <span className={styles.fieldValueEmpty}>—</span>
        )}
      </div>
    );
  }
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <div className={styles.docChip}>
        <a href={doc.uri} target="_blank" rel="noopener noreferrer" className={styles.docChipLink}>{doc.name}</a>
        {redaction === IdRedactionStatus.Redacted && onRedact && (
          <button
            type="button"
            className={`${styles.redactBadge} ${styles.redactBadgeDone}`}
            onClick={onRedact}
            title="הצגת האזורים שהושחרו"
          >
            מושחר
          </button>
        )}
        {onCrop && (
          <button type="button" className={styles.docChipAction} onClick={onCrop}>חיתוך</button>
        )}
        {onReplace && (
          <button type="button" className={styles.docChipAction} onClick={onReplace}>החלפה</button>
        )}
        {onDelete && (
          <button
            type="button"
            className={`${styles.docChipAction} ${styles.docChipActionDanger}`}
            onClick={onDelete}
          >
            מחיקה
          </button>
        )}
        <DownloadFileButton uri={doc.uri} fileName={doc.name} iconSrc={downloadArrow} />
      </div>
    </div>
  );
};

const SectionCard = ({ title, icon, children, grow }: {
  title: string;
  icon: SectionIconName;
  children: ReactNode;
  grow?: boolean;
}) => (
  <div className={grow ? `${styles.section} ${styles.sectionGrow}` : styles.section}>
    <div className={styles.sectionTitle}>
      <SectionIcon name={icon} />
      {title}
    </div>
    {children}
  </div>
);

/**
 * Three columns of details, and the purple column beside them as the fourth: what the fund is set up for and
 * the files attached to it - the things the eye looks for on their own.
 */
const DetailsWithSideColumn = ({ fields, side, columns = 3 }: {
  fields: ReactNode;
  side?: ReactNode[];
  columns?: 1 | 3;
}) => {
  const sideItems = (side ?? []).filter(Boolean);
  const gridClass = columns === 1 ? styles.fieldsColumn : styles.fieldsGrid;
  if (sideItems.length === 0) {
    return <div className={styles.sectionBody}><div className={gridClass}>{fields}</div></div>;
  }
  return (
    <div className={styles.sectionBody}>
      <div className={styles.bodyRow}>
        <div className={columns === 1 ? styles.detailsSideOne : styles.detailsSide}>
          <div className={gridClass}>{fields}</div>
        </div>
        <div className={styles.purpleColumn}>{sideItems}</div>
      </div>
    </div>
  );
};

const isWelfareClassification = (c: FundClassification) =>
  c === FundClassification.Welfare || c === FundClassification.CampaignForWelfare;

const isCommunityClassification = (c: FundClassification) =>
  c === FundClassification.Community || c === FundClassification.CampaignForCommunity;

const PERSON_LABELS: Record<string, string> = {
  israeliId: 'מספר זהות',
  firstName: 'שם פרטי',
  lastName: 'שם משפחה',
  city: 'עיר',
  street: 'רחוב',
  houseNumber: 'מספר בית',
  phone: 'טלפון',
  email: 'דוא"ל',
};

const COORDINATOR_FIELDS = ['israeliId', 'firstName', 'lastName', 'city', 'street', 'houseNumber', 'phone', 'email'] as const;
const SUPPORTED_FIELDS = ['israeliId', 'firstName', 'lastName', 'city', 'street', 'houseNumber', 'email', 'phone'] as const;

/** Names one person in the upload dialog the way the page names them - numbered only when there is more than one. */
const personTitle = (role: string, index: number, total: number) => (total > 1 ? `${role} ${index + 1}` : role);

type ButtonType =
  | 'Edit'
  | 'Approved'
  | 'Rejected'
  | 'Incomplete'
  | 'PendingApproval'
  | 'Deleted'
  | 'CancelledByUser'
  | 'ResetSyncStatus'
  | 'Completed';

function shouldShowButton(
  buttonType: ButtonType,
  status: OpenFundRequestStatus,
  syncStatus?: SyncStatus
): boolean {
  switch (buttonType) {
    case 'Edit':
      // Completed is editable too, but only by the descriptive values - the edit dialog offers exactly the
      // values that may still be changed in the current status.
      return [
        OpenFundRequestStatus.Incomplete,
        OpenFundRequestStatus.CancelledByUser,
        OpenFundRequestStatus.Rejected,
        OpenFundRequestStatus.DeletedByOffice,
        OpenFundRequestStatus.PendingApproval,
        OpenFundRequestStatus.ReturnedFromIncomplete,
        OpenFundRequestStatus.Completed,
      ].includes(status);
    case 'Approved':
      return (
        status === OpenFundRequestStatus.PendingApproval ||
        status === OpenFundRequestStatus.Rejected ||
        status === OpenFundRequestStatus.ReturnedFromIncomplete
      );
    case 'Rejected':
      return (
        status === OpenFundRequestStatus.PendingApproval ||
        status === OpenFundRequestStatus.ReturnedFromIncomplete
      );
    case 'Incomplete':
      return (
        status === OpenFundRequestStatus.PendingApproval ||
        status === OpenFundRequestStatus.ReturnedFromIncomplete
      );
    case 'PendingApproval':
      return (
        [
          OpenFundRequestStatus.Incomplete,
          OpenFundRequestStatus.CancelledByUser,
          OpenFundRequestStatus.Rejected,
          OpenFundRequestStatus.DeletedByOffice,
        ].includes(status) ||
        (status === OpenFundRequestStatus.Approved && syncStatus === SyncStatus.Pending)
      );
    case 'Deleted':
      return (
        [
          OpenFundRequestStatus.Incomplete,
          OpenFundRequestStatus.CancelledByUser,
          OpenFundRequestStatus.PendingApproval,
          OpenFundRequestStatus.Rejected,
          OpenFundRequestStatus.ReturnedFromIncomplete,
        ].includes(status) ||
        (status === OpenFundRequestStatus.Approved &&
          (syncStatus === SyncStatus.Pending || syncStatus === SyncStatus.Failed))
      );
    case 'CancelledByUser':
      return (
        status === OpenFundRequestStatus.Incomplete ||
        status === OpenFundRequestStatus.PendingApproval ||
        status === OpenFundRequestStatus.ReturnedFromIncomplete
      );
    case 'ResetSyncStatus':
      return status === OpenFundRequestStatus.Approved && syncStatus === SyncStatus.Failed;
    case 'Completed':
      // Normally the external sync moves Approved -> Completed; the manual "create fund" button is
      // only for the sync-failure recovery case. In local development (no external sync running),
      // also allow completing any Approved request so the final emails can be tested.
      return (
        (status === OpenFundRequestStatus.Approved && syncStatus === SyncStatus.Failed) ||
        (import.meta.env.DEV && status === OpenFundRequestStatus.Approved)
      );
    default:
      return false;
  }
}

const OpenFundRequestViewPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuthContext();

  const requestId = (location.state as { requestId?: number })?.requestId;

  const { data: request, isLoading } = useGetAuthApi<OpenFundRequestDtoIn>(
    requestId ? `funds/creation/open-fund-request?requestId=${requestId}` : ''
  );

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const [rejectReason, setRejectReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [incompleteReason, setIncompleteReason] = useState('');
  const [showIncompleteDialog, setShowIncompleteDialog] = useState(false);
  const [fundIdInput, setFundIdInput] = useState('');
  const [showApproveAskDialog, setShowApproveAskDialog] = useState(false);
  const [showApproveFundIdDialog, setShowApproveFundIdDialog] = useState(false);
  const [showCreateFundDialog, setShowCreateFundDialog] = useState(false);
  // Which identity document the office is uploading a replacement for. Not tied to the request's status:
  // an ID copy that cannot be read has to be replaceable at any point of the request's life.
  const [documentTarget, setDocumentTarget] = useState<IdDocumentTarget | null>(null);
  // Which coordinator's ID appendix the office is about to destroy, and the wording of the question asked
  // before it happens. Null while no such question is open.
  const [appendixToDelete, setAppendixToDelete] = useState<{ personIndex: number; personTitle: string } | null>(null);
  const [isDeletingAppendix, setIsDeletingAppendix] = useState(false);
  // Whose ID card the office is blacking out the issue date on. Null while no such screen is open.
  const [redactionTarget, setRedactionTarget] = useState<RedactionTarget | null>(null);
  // Which copy the office is cutting the ID appendix out of. Null while no such screen is open.
  const [cropTarget, setCropTarget] = useState<CropTarget | null>(null);

  const { mutateAsync: approveRequest } = usePutAuthApi<void>(
    `funds/creation/${requestId}/status/approved`
  );
  const { mutateAsync: rejectRequest } = usePutAuthApi<void, { rejectReason: string }>(
    `funds/creation/${requestId}/status/rejected`
  );
  const { mutateAsync: incompleteRequest } = usePutAuthApi<void, { reason: string }>(
    `funds/creation/${requestId}/status/incomplete`
  );
  const { mutateAsync: pendingApprovalRequest } = usePutAuthApi<void>(
    `funds/creation/${requestId}/status/pending-approval`
  );
  const { mutateAsync: deleteRequest } = usePutAuthApi<void>(
    `funds/creation/${requestId}/status/deleted-by-office`
  );
  const { mutateAsync: cancelByUserRequest } = usePutAuthApi<void>(
    `funds/creation/${requestId}/status/cancelled-by-user`
  );
  const { mutateAsync: completeRequest } = usePutAuthApi<void>(
    `funds/creation/${requestId}/status/completed`
  );
  const { mutateAsync: resetSyncStatus } = usePutAuthApi<void>(
    `funds/creation/${requestId}/reset-sync-status`
  );
  // The target is part of the address rather than of a body, because a DELETE carries no body here. The
  // hook is rebuilt on every render, so it always addresses whichever appendix the open question is about.
  const { mutateAsync: deleteCoordinatorAppendix } = useDeleteAuthApi<void>(
    appendixToDelete
      ? `funds/creation/${requestId}/document?personRole=${OpenFundRequestPersonRole.Coordinator}`
        + `&personIndex=${appendixToDelete.personIndex}&documentSlot=${OpenFundRequestDocumentSlot.IdAppendix}`
      : ''
  );

  const navigateBack = useCallback(() => {
    navigate('/management/open-fund-requests');
  }, [navigate]);

  const invalidateAndGoBack = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['funds/creation/open-fund-requests-list'] });
    queryClient.invalidateQueries({ queryKey: [`funds/creation/open-fund-request?requestId=${requestId}`] });
    navigateBack();
  }, [navigateBack, requestId]);

  const handleError = useCallback((error: unknown) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const err = error as any;
    const status = err?.response?.status ?? err?.status;
    const data = err?.response?.data ?? err?.error;

    if (status === 409 && data) {
      const { fromStatus, toStatus, message } = data;
      if (fromStatus && toStatus && message) {
        const translatedFrom = t(`openFundRequests.status.${fromStatus}`);
        const translatedTo = t(`openFundRequests.status.${toStatus}`);
        if (fromStatus === toStatus) {
          alert(`שגיאה ${status}: ${message}, הבקשה כבר בסטטוס ${translatedFrom}`);
        } else if (fromStatus === OpenFundRequestStatus.Approved) {
          alert(`שגיאה ${status}: ${message}`);
        } else {
          alert(`שגיאה ${status}: ${message} מסטטוס ${translatedFrom} לסטטוס ${translatedTo}`);
        }
        return;
      }
      alert(`שגיאה ${status}: ${typeof data === 'string' ? data : JSON.stringify(data)}`);
      return;
    }

    const message = err?.response?.data?.message ?? err?.message ?? 'שגיאה לא ידועה';
    alert(status ? `שגיאה ${status}: ${message}` : message);
  }, [t]);

  const handleSaved = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [`funds/creation/open-fund-request?requestId=${requestId}`] });
    setShowEditDialog(false);
  }, [requestId]);

  const handleDocumentReplaced = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [`funds/creation/open-fund-request?requestId=${requestId}`] });
  }, [requestId]);

  const handleDeleteAppendix = useCallback(async () => {
    if (!appendixToDelete) return;
    try {
      setIsDeletingAppendix(true);
      await deleteCoordinatorAppendix();
      setAppendixToDelete(null);
      queryClient.invalidateQueries({ queryKey: [`funds/creation/open-fund-request?requestId=${requestId}`] });
    } catch (error) {
      handleError(error);
    } finally {
      setIsDeletingAppendix(false);
    }
  }, [appendixToDelete, deleteCoordinatorAppendix, handleError, requestId]);

  /**
   * Saves the request as the fund-opening PDF - the same document the applicant and the office get by mail.
   * The API builds it from the request's current details, so it also covers printing: the office prints the
   * downloaded file rather than the screen, which is what a paper copy of the form has to look like.
   */
  const handleDownloadPdf = useCallback(async () => {
    if (isDownloadingPdf) return;
    setIsDownloadingPdf(true);
    try {
      await downloadFileFromApi(
        `funds/creation/${requestId}/pdf`,
        `בקשה-לפתיחת-פרויקט-${requestId}.pdf`,
        currentUser?.token ?? ''
      );
    } catch (error) {
      handleError(error);
    } finally {
      setIsDownloadingPdf(false);
    }
  }, [currentUser?.token, handleError, isDownloadingPdf, requestId]);

  const handleApproveAutoId = useCallback(async () => {
    try {
      setIsUpdatingStatus(true);
      setShowApproveAskDialog(false);
      await approveRequest({});
      invalidateAndGoBack();
    } catch (error) {
      handleError(error);
    } finally {
      setIsUpdatingStatus(false);
    }
  }, [approveRequest, invalidateAndGoBack, handleError]);

  const handleApproveWithFundId = useCallback(async () => {
    const id = Number(fundIdInput);
    if (!fundIdInput.trim() || isNaN(id)) return;
    try {
      setIsUpdatingStatus(true);
      await approveRequest({ params: { fundId: id } });
      setShowApproveFundIdDialog(false);
      invalidateAndGoBack();
    } catch (error) {
      handleError(error);
      setShowApproveFundIdDialog(false);
    } finally {
      setIsUpdatingStatus(false);
    }
  }, [approveRequest, fundIdInput, invalidateAndGoBack, handleError]);

  const handleReject = useCallback(async () => {
    if (!rejectReason.trim()) return;
    try {
      setIsUpdatingStatus(true);
      await rejectRequest({ body: { rejectReason } });
      setShowRejectDialog(false);
      invalidateAndGoBack();
    } catch (error) {
      handleError(error);
      setShowRejectDialog(false);
    } finally {
      setIsUpdatingStatus(false);
    }
  }, [rejectRequest, rejectReason, invalidateAndGoBack, handleError]);

  const handleIncomplete = useCallback(async () => {
    if (!incompleteReason.trim()) return;
    try {
      setIsUpdatingStatus(true);
      await incompleteRequest({ body: { reason: incompleteReason } });
      setShowIncompleteDialog(false);
      invalidateAndGoBack();
    } catch (error) {
      handleError(error);
      setShowIncompleteDialog(false);
    } finally {
      setIsUpdatingStatus(false);
    }
  }, [incompleteRequest, incompleteReason, invalidateAndGoBack, handleError]);

  const handleStatusAction = useCallback(async (action: () => Promise<unknown>) => {
    try {
      setIsUpdatingStatus(true);
      await action();
      invalidateAndGoBack();
    } catch (error) {
      handleError(error);
    } finally {
      setIsUpdatingStatus(false);
    }
  }, [invalidateAndGoBack, handleError]);

  const handleCreateFund = useCallback(async () => {
    try {
      setIsUpdatingStatus(true);
      const fundId = fundIdInput ? Number(fundIdInput) : request?.fundId;
      await completeRequest({ params: { fundId } });
      setShowCreateFundDialog(false);
      invalidateAndGoBack();
    } catch (error) {
      handleError(error);
      setShowCreateFundDialog(false);
    } finally {
      setIsUpdatingStatus(false);
    }
  }, [completeRequest, fundIdInput, request?.fundId, invalidateAndGoBack, handleError]);

  const formatDate = (dateStr?: string) => (dateStr ? new Date(dateStr).toLocaleDateString('he-IL') : '');

  // A consent is a record of a moment, so it is shown with its time and not only its date.
  const formatDateTime = (dateStr?: string) =>
    dateStr ? new Date(dateStr).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' }) : '';

  if (isLoading || !request) {
    return <div className={styles.container}>{t('loading')}</div>;
  }

  const classification = request.fundClassification;
  const isWelfare = isWelfareClassification(classification);
  const isCommunity = isCommunityClassification(classification);
  const goalsText = request.fundGoals.map((goal) => t(`openFundRequest.fundGoals.${goal}`)).join(', ');

  const hasStatusActions = (['Approved', 'Rejected', 'Incomplete', 'PendingApproval', 'Deleted', 'CancelledByUser', 'ResetSyncStatus', 'Completed'] as const)
    .some((action) => shouldShowButton(action, request.status, request.syncStatus));

  /**
   * Where one person's ID-card copy stands on being blacked out (HAT-1303). Undefined on a request whose
   * copies predate the feature, and the tiles then behave exactly as they always did.
   */
  const redactionOf = (personRole: OpenFundRequestPersonRole, personIndex: number) =>
    request.idCardRedactions?.find(
      (entry) => entry.personRole === personRole && entry.personIndex === personIndex,
    )?.status;

  // The server refuses to approve a request whose ID cards still show their issue date, so the button says
  // so before it is pressed rather than answering with an error afterwards.
  const pendingRedactions = (request.idCardRedactions ?? [])
    .filter((entry) => entry.status === IdRedactionStatus.Pending).length;

  return (
    <div className={styles.container}>
      {/* The title centred in the page, with the way back beside it. The arrow is the app's own arrowRight
          geometry, drawn inline so it takes the link's colour; in RTL, back points right. */}
      <div className={styles.pageHeader}>
        <button type="button" className={styles.headerLink} onClick={navigateBack}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="16.5312" y1="10.5519" x2="3.78125" y2="10.5519" />
            <path d="M11.3016 5.46875L17.0308 10.625L11.3016 15.7812" />
          </svg>
          חזרה
        </button>

        <h2 className={`heading-h3-semibold text-(--font-primary) ${styles.pageTitle}`}>
          {t(`openFundRequests.classificationTitles.${classification}`)}
        </h2>

        {/* Download sits beside עריכה rather than in its own grid column, so the title stays centred on the
            page whether or not this request can still be edited. */}
        <div className={styles.headerActions}>
          {shouldShowButton('Edit', request.status, request.syncStatus) && (
            <button
              type="button"
              className={styles.headerLink}
              onClick={() => setShowEditDialog(true)}
              disabled={isUpdatingStatus}
            >
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.6}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z" />
                <path d="M14.5 5.5l4 4" />
              </svg>
              עריכה
            </button>
          )}

          <button
            type="button"
            className={styles.headerLink}
            onClick={handleDownloadPdf}
            disabled={isDownloadingPdf}
            title="הורדת טופס פתיחת הפרויקט כקובץ PDF"
          >
            {/* Drawn inline, like the other header glyphs, so it takes the link's colour. */}
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 3.5v11" />
              <path d="M7.5 10.5L12 15l4.5-4.5" />
              <path d="M4.5 18.5h15" />
            </svg>
            {isDownloadingPdf ? 'מוריד...' : 'הורדת הטופס'}
          </button>
        </div>
      </div>

      {request.status === OpenFundRequestStatus.Approved &&
        request.syncStatus === SyncStatus.Failed && (
          <div className={styles.syncError}>
            ❌ {request.syncFailureReason &&
              t(`openFundRequests.syncFailureReason.${request.syncFailureReason}`)}
          </div>
        )}

      {/* What the office asked for when it sent the request back. Shown while the applicant still owes an
          answer, and kept once the answer arrives - that is when it is read, to check one against the other. */}
      {request.incompleteReason &&
        (request.status === OpenFundRequestStatus.Incomplete ||
          request.status === OpenFundRequestStatus.ReturnedFromIncomplete) && (
          <div className={styles.incompleteNote}>
            <div className={styles.incompleteNoteHeader}>
              <span>
                {request.status === OpenFundRequestStatus.Incomplete
                  ? 'נשלחה למגיש בקשה להשלמת מסמכים'
                  : 'ההשלמה שנתבקשה מהמגיש'}
              </span>
              {request.incompleteRequestedAt && (
                <span className={styles.incompleteNoteDate}>
                  {formatDate(request.incompleteRequestedAt)}
                </span>
              )}
            </div>
            <div className={styles.incompleteNoteText}>{request.incompleteReason}</div>
          </div>
        )}

      <div className={styles.form}>
        {/* פרטי הקרן, ובטור הרביעי מה שמוגדר בה */}
        <SectionCard title="פרטי הפרויקט" icon="fund">
          <DetailsWithSideColumn
            side={[
              <SettingTile key="nedarim" text="ממשק אישי בנדרים פלוס" on={request.personalInterfaceInNedarimPlus} />,
              <SettingTile key="crowdfunding" text="קמפיין למימון המונים" on={request.crowdfundingCampaign} />,
            ]}
            fields={(
              <>
                <Field label="מספר בקשה" value={request.requestId} />
                <Field label="מספר פרויקט" value={request.fundId || undefined} />
                <Field label="שם הפרויקט" value={request.fundName} />
                <Field label="סיווג הפרויקט" value={t(`openFundRequests.fundClassificationNames.${classification}`)} />
                <Field label="סטטוס" value={t(`openFundRequests.status.${request.status}`)} />
                <Field label="תאריך הגשת הבקשה" value={formatDate(request.creationTime)} />
                <Field label="תאריך שינוי אחרון" value={formatDate(request.lastModifiedTime)} />
                {isCommunity && (
                  <Field
                    label={request.responsibleParty === 'charityTrustee' ? 'שם גבאי צדקה' : 'שם הקהילה'}
                    value={request.responsibleEntityName}
                  />
                )}
              </>
            )}
          />
        </SectionCard>

        {/* מטרת הקרן ומגיש הבקשה, אחד ליד השני */}
        <div className={styles.sectionRow}>
          {(isWelfare || isCommunity) && (
            <SectionCard title="מטרת הפרויקט" icon="purpose" grow>
              <DetailsWithSideColumn
                columns={1}
                fields={(
                  <>
                    <Field label="סיכום הסיבה" value={request.reasonSummary} wide />
                    <Field label="מטרות הפרויקט" value={goalsText} wide />
                    {request.fundGoals.includes(FundGoals.Other) && (
                      <Field label='פירוט מטרת ה"אחר"' value={request.otherGoalDescription} wide />
                    )}
                  </>
                )}
              />
            </SectionCard>
          )}

          <SectionCard title="מגיש הבקשה" icon="applicant" grow>
            <DetailsWithSideColumn
              columns={1}
              side={[<DocChip key="signature" label="חתימת מגיש הבקשה" document={request.signature} />]}
              fields={(
                <>
                  <Field label="שם פרטי" value={request.applicantDetails.firstName} />
                  <Field label="שם משפחה" value={request.applicantDetails.lastName} />
                  <Field label="דואר אלקטרוני" value={request.applicantDetails.email} />
                  {/* Requests sent before the consent was asked for have nothing here, and show a dash. */}
                  <Field
                    label="הסכמה למדיניות הפרטיות"
                    value={
                      request.privacyPolicyAccepted
                        ? [
                            `אושרה ${formatDateTime(request.privacyPolicyAcceptedAt)}`,
                            request.privacyPolicyVersion && `גרסה ${request.privacyPolicyVersion}`,
                          ]
                            .filter(Boolean)
                            .join(' · ')
                        : undefined
                    }
                    wide
                  />
                </>
              )}
            />
          </SectionCard>
        </div>

        {/* רכזים */}
        <SectionCard title={request.coordinators.length > 1 ? 'רכזים' : 'רכז הפרויקט'} icon="coordinator">
          {request.coordinators.map((coordinator, i) => (
            <div key={i} className={styles.personBlock}>
              {request.coordinators.length > 1 && (
                <span className={styles.mediumTitle}>רכז {i + 1}</span>
              )}
              <DetailsWithSideColumn
                side={[
                  <DocChip
                    key="id"
                    label="צילום תעודת זהות"
                    document={coordinator.idCardPhotocopy}
                    onReplace={() => setDocumentTarget({
                      personRole: OpenFundRequestPersonRole.Coordinator,
                      personIndex: i,
                      documentSlot: OpenFundRequestDocumentSlot.IdCard,
                      title: `צילום תעודת זהות של ${personTitle('רכז', i, request.coordinators.length)}`,
                      currentDocument: coordinator.idCardPhotocopy,
                    })}
                    redaction={redactionOf(OpenFundRequestPersonRole.Coordinator, i)}
                    onRedact={() => setRedactionTarget({
                      personRole: OpenFundRequestPersonRole.Coordinator,
                      personIndex: i,
                      title: `צילום תעודת זהות של ${personTitle('רכז', i, request.coordinators.length)}`,
                    })}
                    // The one tile that can be carrying a document the office is not meant to keep while
                    // itself having to stay: the appendix photographed onto the coordinator's ID card
                    // (HAT-1302). The supported's copies are not offered this - their appendix is required
                    // by the welfare flow and is kept.
                    onCrop={() => setCropTarget({
                      personRole: OpenFundRequestPersonRole.Coordinator,
                      personIndex: i,
                      documentSlot: OpenFundRequestDocumentSlot.IdCard,
                      title: `צילום תעודת זהות של ${personTitle('רכז', i, request.coordinators.length)}`,
                    })}
                  />,
                  <DocChip
                    key="appendix"
                    label="צילום ספח ת.ז."
                    document={coordinator.idAppendixPhotocopy}
                    onReplace={() => setDocumentTarget({
                      personRole: OpenFundRequestPersonRole.Coordinator,
                      personIndex: i,
                      documentSlot: OpenFundRequestDocumentSlot.IdAppendix,
                      title: `צילום ספח ת.ז. של ${personTitle('רכז', i, request.coordinators.length)}`,
                      currentDocument: coordinator.idAppendixPhotocopy,
                    })}
                    // The appendix is asked for so the office can read it once, and is destroyed after -
                    // unlike the ID card beside it, which stays with the fund's record (HAT-1302).
                    onDelete={() => setAppendixToDelete({
                      personIndex: i,
                      personTitle: personTitle('רכז', i, request.coordinators.length),
                    })}
                  />,
                  ...(coordinator.additionalDocuments ?? []).filter((doc) => doc?.uri).map((doc, docIdx) => (
                    <DocChip key={`extra-${docIdx}`} label="מסמך נוסף" document={doc} />
                  )),
                ]}
                fields={(
                  <>
                    {COORDINATOR_FIELDS.map((field) => (
                      <Field key={field} label={PERSON_LABELS[field]} value={coordinator[field]} />
                    ))}
                    {coordinator.relationToSupported && (
                      <Field
                        label="קשר הרכז לנתמך"
                        value={t(`openFundRequest.coordinatorRelationOptions.${coordinator.relationToSupported}`)}
                      />
                    )}
                    {coordinator.spouseFirstName && (
                      <Field label="שם בן/בת הזוג" value={coordinator.spouseFirstName} />
                    )}
                    {coordinator.spouseIsraeliId && (
                      <Field label='ת.ז. בן/בת הזוג' value={coordinator.spouseIsraeliId} />
                    )}
                  </>
                )}
              />
            </div>
          ))}
        </SectionCard>

        {/* נתמכים */}
        {isWelfare && request.supporteds.length > 0 && (
          <SectionCard title={request.supporteds.length > 1 ? 'נתמכים' : 'הנתמך'} icon="supported">
            {request.supporteds.map((supported, i) => (
              <div key={i} className={styles.personBlock}>
                {request.supporteds.length > 1 && (
                  <span className={styles.mediumTitle}>נתמך {i + 1}</span>
                )}
                <DetailsWithSideColumn
                  side={[
                    <DocChip
                      key="id"
                      label="צילום תעודת זהות"
                      document={supported.idCardPhotocopy}
                      onReplace={() => setDocumentTarget({
                        personRole: OpenFundRequestPersonRole.Supported,
                        personIndex: i,
                        documentSlot: OpenFundRequestDocumentSlot.IdCard,
                        title: `צילום תעודת זהות של ${personTitle('נתמך', i, request.supporteds.length)}`,
                        currentDocument: supported.idCardPhotocopy,
                      })}
                      redaction={redactionOf(OpenFundRequestPersonRole.Supported, i)}
                      onRedact={() => setRedactionTarget({
                        personRole: OpenFundRequestPersonRole.Supported,
                        personIndex: i,
                        title: `צילום תעודת זהות של ${personTitle('נתמך', i, request.supporteds.length)}`,
                      })}
                    />,
                    <DocChip
                      key="appendix"
                      label="צילום ספח"
                      document={supported.idAppendixPhotocopy}
                      onReplace={() => setDocumentTarget({
                        personRole: OpenFundRequestPersonRole.Supported,
                        personIndex: i,
                        documentSlot: OpenFundRequestDocumentSlot.IdAppendix,
                        title: `צילום ספח ת.ז. של ${personTitle('נתמך', i, request.supporteds.length)}`,
                        currentDocument: supported.idAppendixPhotocopy,
                      })}
                    />,
                    ...(supported.additionalDocuments ?? []).filter((doc) => doc?.uri).map((doc, docIdx) => (
                      <DocChip key={`extra-${docIdx}`} label="מסמך נוסף" document={doc} />
                    )),
                    // The supported's two settings belong to the first supported, not to each one
                    ...(i === 0 && request.supportedAwareOfFund != null ? [
                      <SettingTile key="aware" text="הנתמך מודע להקמת הפרויקט" on={request.supportedAwareOfFund} />,
                      <SettingTile key="canView" text="הרשאת צפייה בפרויקט לנתמך" on={request.supportedCanViewFund} />,
                    ] : []),
                  ]}
                  fields={(
                    <>
                      {SUPPORTED_FIELDS.map((field) => (
                        <Field key={field} label={PERSON_LABELS[field]} value={supported[field]} />
                      ))}
                      <Field
                        label="הקשר המשפחתי לנתמך הראשון"
                        value={supported.relationshipToFirstSupported
                          ? t(`openFundRequest.relationships.${supported.relationshipToFirstSupported}`)
                          : undefined}
                      />
                    </>
                  )}
                />
              </div>
            ))}
          </SectionCard>
        )}
      </div>

      {/* The status actions. Editing and going back now live beside the title, so a request with no status
          action left - a fund that has already been created - has no bar at all. */}
      {hasStatusActions && (
      <div className={styles.actionsBar}>
        {shouldShowButton('Approved', request.status, request.syncStatus) && (
          <Button
            variant="primary"
            onClick={() => { setFundIdInput(''); setShowApproveAskDialog(true); }}
            disabled={isUpdatingStatus || pendingRedactions > 0}
            title={pendingRedactions > 0
              ? 'יש להשחיר את תאריך ההנפקה בצילומי תעודת הזהות לפני אישור הבקשה'
              : undefined}
          >
            אשר
          </Button>
        )}
        {/* Said beside the button rather than only on hovering it: the button is disabled, and a disabled
            button that explains itself only in a tooltip explains itself to nobody on a touch screen. */}
        {pendingRedactions > 0 && shouldShowButton('Approved', request.status, request.syncStatus) && (
          <span className={styles.redactGateNote}>
            {pendingRedactions === 1
              ? 'צילום תעודת זהות אחד ממתין להשחרת תאריך ההנפקה'
              : `${pendingRedactions} צילומי תעודת זהות ממתינים להשחרת תאריך ההנפקה`}
          </span>
        )}
        {shouldShowButton('Rejected', request.status, request.syncStatus) && (
          <Button variant="primary" className="!bg-red-600" onClick={() => { setRejectReason(''); setShowRejectDialog(true); }} disabled={isUpdatingStatus}>דחה</Button>
        )}
        {shouldShowButton('Incomplete', request.status, request.syncStatus) && (
          <Button variant="outline" onClick={() => { setIncompleteReason(''); setShowIncompleteDialog(true); }} disabled={isUpdatingStatus}>השלמת מסמכים</Button>
        )}
        {shouldShowButton('PendingApproval', request.status, request.syncStatus) && (
          <Button variant="outline" onClick={() => handleStatusAction(() => pendingApprovalRequest({}))} disabled={isUpdatingStatus}>מחכה לאישור</Button>
        )}
        {shouldShowButton('Deleted', request.status, request.syncStatus) && (
          <Button variant="outline" onClick={() => handleStatusAction(() => deleteRequest({}))} disabled={isUpdatingStatus}>מחק</Button>
        )}
        {shouldShowButton('CancelledByUser', request.status, request.syncStatus) && (
          <Button variant="outline" onClick={() => handleStatusAction(() => cancelByUserRequest({}))} disabled={isUpdatingStatus}>בטל(ע"י המשתמש)</Button>
        )}
        {shouldShowButton('ResetSyncStatus', request.status, request.syncStatus) && (
          <Button variant="outline" onClick={() => handleStatusAction(() => resetSyncStatus({}))} disabled={isUpdatingStatus}>העבר להמתנה לסנכרון</Button>
        )}
        {shouldShowButton('Completed', request.status, request.syncStatus) && (
          <Button variant="primary" className="!bg-amber-500" onClick={() => { setFundIdInput(''); setShowCreateFundDialog(true); }} disabled={isUpdatingStatus}>צור פרויקט</Button>
        )}
      </div>
      )}

      <OpenFundRequestEditDialog
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        request={request}
        onSaved={handleSaved}
      />

      {documentTarget && (
        <ReplaceIdDocumentDialog
          requestId={request.requestId}
          target={documentTarget}
          onClose={() => setDocumentTarget(null)}
          onSaved={handleDocumentReplaced}
        />
      )}

      {redactionTarget && (
        <RedactIdDocumentDialog
          requestId={request.requestId}
          target={redactionTarget}
          onClose={() => setRedactionTarget(null)}
          onSaved={handleDocumentReplaced}
        />
      )}

      {cropTarget && (
        <CropIdDocumentDialog
          requestId={request.requestId}
          target={cropTarget}
          onClose={() => setCropTarget(null)}
          onSaved={handleDocumentReplaced}
        />
      )}

      {/* Destroying a coordinator's ID appendix. Spelled out rather than asked as a plain "are you sure?",
          because there is nothing to undo it with afterwards - not a copy in the fund, not one in storage. */}
      <ConfirmationDialog
        open={!!appendixToDelete}
        onOpenChange={(open) => { if (!open) setAppendixToDelete(null); }}
        variant="danger"
        title="מחיקת צילום הספח"
        message={appendixToDelete
          ? `צילום הספח של ${appendixToDelete.personTitle} יימחק לצמיתות - מהבקשה, מהפרויקט ומהאחסון - ולא ניתן יהיה לשחזר אותו. `
            + 'צילום תעודת הזהות יישאר במקומו. למחוק?'
          : undefined}
        confirmText="openFundRequests.deleteIdAppendix.confirm"
        onConfirm={handleDeleteAppendix}
        onCancel={() => setAppendixToDelete(null)}
        isLoading={isDeletingAppendix}
      />

      {/* Approve Step 1: Ask if user wants to set a custom fund number */}
      <ConfirmationDialog
        open={showApproveAskDialog}
        onOpenChange={setShowApproveAskDialog}
        title="האם תרצה להגדיר את מספר הפרויקט החדש?"
        confirmText="openFundRequests.approveDialog.no"
        cancelText="openFundRequests.approveDialog.yes"
        onConfirm={handleApproveAutoId}
        onCancel={() => {
          setShowApproveAskDialog(false);
          setFundIdInput('');
          setShowApproveFundIdDialog(true);
        }}
        isLoading={isUpdatingStatus}
      />

      {/* Approve Step 2: Enter fund ID */}
      <CustomDialog open={showApproveFundIdDialog} setOpen={setShowApproveFundIdDialog} onBtnClick={handleApproveWithFundId} btnText="openFundRequests.actions.approve" btnDisabled={isUpdatingStatus || !fundIdInput.trim()}>
        <div style={{ direction: 'rtl', textAlign: 'right', width: '100%' }}>
          <h3 className="heading-h4-semibold mb-4">מספר מזהה לפרויקט</h3>
          <label className="body-md-regular mb-2 block">נא הכנס את מספר הפרויקט המבוקש</label>
          <input
            className={styles.input}
            type="number"
            value={fundIdInput}
            onChange={(e) => setFundIdInput(e.target.value)}
            placeholder="מספר פרויקט"
          />
          {fundIdInput && isNaN(Number(fundIdInput)) && <div className={styles.errorText}>נא הכנס מספר תקין</div>}
        </div>
      </CustomDialog>

      {/* Reject Dialog */}
      <CustomDialog open={showRejectDialog} setOpen={setShowRejectDialog} onBtnClick={handleReject} btnText="openFundRequests.actions.reject" btnDisabled={isUpdatingStatus || !rejectReason.trim()}>
        <div style={{ direction: 'rtl', textAlign: 'right', width: '100%' }}>
          <h3 className="heading-h4-semibold mb-4">סיבת דחיית הבקשה</h3>
          <label className="body-md-regular mb-2 block">נא הכנס סיבה לדחיית הבקשה לפתיחת פרויקט</label>
          <textarea
            className={styles.input}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            style={{ minHeight: 80 }}
          />
          {!rejectReason.trim() && <div className={styles.errorText}>יש להזין סיבת דחייה</div>}
        </div>
      </CustomDialog>

      {/* Incomplete (missing documents) Dialog */}
      <CustomDialog open={showIncompleteDialog} setOpen={setShowIncompleteDialog} onBtnClick={handleIncomplete} btnText="openFundRequests.actions.incomplete" btnDisabled={isUpdatingStatus || !incompleteReason.trim()}>
        <div style={{ direction: 'rtl', textAlign: 'right', width: '100%' }}>
          <h3 className="heading-h4-semibold mb-4">השלמת מסמכים</h3>
          <label className="body-md-regular mb-2 block">נא פרט מה חסר או מה יש לתקן. פירוט זה יישלח למגיש הבקשה יחד עם קישור להשלמה.</label>
          <textarea
            className={styles.input}
            value={incompleteReason}
            onChange={(e) => setIncompleteReason(e.target.value)}
            style={{ minHeight: 80 }}
          />
          {!incompleteReason.trim() && <div className={styles.errorText}>יש להזין פירוט מה נדרש להשלים</div>}
        </div>
      </CustomDialog>

      {/* Create Fund Dialog */}
      <CustomDialog open={showCreateFundDialog} setOpen={setShowCreateFundDialog} onBtnClick={handleCreateFund} btnText="confirm" btnDisabled={isUpdatingStatus || (!request.fundId && !fundIdInput.trim())}>
        <div style={{ direction: 'rtl', textAlign: 'right', width: '100%' }}>
          <h3 className="heading-h4-semibold mb-4">אזהרה</h3>
          <p className="body-lr-regular mb-4">
            הפרויקט ייווצר במערכת הזו, אבל לא במערכת APT.
            <br />
            האם ברצונך להמשיך?
          </p>
          {!request.fundId && (
            <>
              <label className="body-md-regular mb-2 block">נא הכנס את מספר הפרויקט המבוקש</label>
              <input
                className={styles.input}
                type="number"
                value={fundIdInput}
                onChange={(e) => setFundIdInput(e.target.value)}
                placeholder="מספר פרויקט"
              />
            </>
          )}
        </div>
      </CustomDialog>
    </div>
  );
};

export default OpenFundRequestViewPage;
